/**
 * Shared helpers for Bond Retrieval and Archives pages
 */

/**
 * Format ISO timestamp to datetime-local input format (YYYY-MM-DDTHH:mm)
 * @param {string} iso - ISO timestamp
 * @returns {string} Formatted datetime string or empty string
 */
export const formatIsoToDatetimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Format date to MM/DD/YYYY format
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return "";
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
};

/**
 * Apply filters to bonds list
 * @param {Array} bonds - Array of bond objects
 * @param {Object} filters - Filter criteria object
 * @returns {Array} Filtered bonds array
 */
export const applyBondFilters = (bonds, filters) => {
  if (!Array.isArray(bonds)) return [];
  
  return bonds.filter((bond) => {
    // Bond number filter
    if (filters.bondNumber && !bond.bond_number?.includes(filters.bondNumber)) {
      return false;
    }
    
    // Bond type filter
    if (filters.bondType && bond.bond_type !== filters.bondType) {
      return false;
    }
    
    // Status filter
    if (filters.status && bond.status !== filters.status) {
      return false;
    }
    
    // Date range filter
    if (filters.startDate && bond.created_at < filters.startDate) {
      return false;
    }
    if (filters.endDate && bond.created_at > filters.endDate) {
      return false;
    }
    
    return true;
  });
};

/**
 * Calculate endorsement value based on type
 * @param {Object} endorsement - Endorsement object
 * @param {string} type - Endorsement type
 * @returns {string} Calculated endorsement value
 */
export const calculateEndorsementValue = (endorsement, type) => {
  if (!endorsement) return "";
  
  const isAmountType = ["Increase", "Decrease"].includes(type);
  
  if (isAmountType && endorsement.amount != null) {
    return Number(endorsement.amount).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    });
  }
  
  return endorsement.value || "";
};

/**
 * Get bond status display information
 * @param {Object} bond - Bond object
 * @returns {Object} {status: string, color: string, label: string}
 */
export const getBondStatusInfo = (bond) => {
  if (!bond) return { status: "unknown", color: "gray", label: "Unknown" };
  
  const status = bond.status || "";
  
  const statusMap = {
    draft: { color: "gray", label: "Draft" },
    submitted: { color: "blue", label: "Submitted" },
    approved: { color: "green", label: "Approved" },
    rejected: { color: "red", label: "Rejected" },
    pending: { color: "yellow", label: "Pending" },
  };
  
  return statusMap[status.toLowerCase()] || { status, color: "gray", label: status };
};

/**
 * Map nullable boolean indemnity flag to Yes/No form value.
 * @param {boolean|null|undefined} value
 * @returns {string}
 */
export const mapIndemnityReceivedToYesNo = (value) => {
  if (value === null || value === undefined) return "";
  return value ? "Yes" : "No";
};

/**
 * Sort bonds by specified field and order
 * @param {Array} bonds - Array of bond objects
 * @param {string} orderBy - Field to sort by
 * @param {string} order - Sort order ('asc' or 'desc')
 * @returns {Array} Sorted bonds array
 */
/** Human-readable role label for bond data-correction requester_info. */
export function getDataCorrectionRoleLabel(role) {
  if (role === "super_user") return "System Admin";
  if (role === "internal_user") return "Internal User";
  if (role === "law_firm_admin") return "Law Firm Admin";
  if (role === "law_firm_user") return "Law Firm User";
  if (role === "professional_guardian_user" || role === "professional guardian") {
    return "Professional Guardian User";
  }
  if (role === "pg_contact") return "PG Contact";
  return role || "User";
}

export const sortBonds = (bonds, orderBy, order = "asc") => {
  if (!Array.isArray(bonds)) return [];
  
  return [...bonds].sort((a, b) => {
    const aVal = a[orderBy];
    const bVal = b[orderBy];
    
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    
    if (typeof aVal === "string") {
      return order === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    
    return order === "asc" ? aVal - bVal : bVal - aVal;
  });
};
